import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkEmoji from 'remark-emoji';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PostViewPage = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/posts/${id}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
      toast.error('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMMM dd, yyyy');
    } catch {
      return 'Recently';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading post...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" data-testid="post-not-found">
        <p className="text-gray-500 text-lg mb-4">Post not found</p>
        <Link to="/">
          <Button variant="outline">
            <ArrowLeft className="mr-2 w-4 h-4" /> Back to Posts
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen " data-testid="post-view-page">
      {/* Header */}
      <div className="border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/">
            <Button variant="ghost" className="mb-4 -ml-4" data-testid="back-button">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Posts
            </Button>
          </Link>
        </div>
      </div>

      {/* Content */}
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Featured Image */}
          {post.featured_image && (
            <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
              <img
                src={post.featured_image}
                alt={post.title}
                className="w-full h-96 object-cover"
                data-testid="featured-image"
              />
            </div>
          )}

          {/* Post Header */}
          <header className="mb-8">
            {/* Status Badge */}
            <div className="flex items-center gap-2 mb-4">
              <Badge
                variant={post.status === 'published' ? 'default' : 'secondary'}
                className={post.status === 'published' ? 'bg-green-500' : 'bg-yellow-500'}
                data-testid="post-status"
              >
                {post.status}
              </Badge>
            </div>

            {/* Title */}
            <h1 className="text-4xl sm:text-5xl font-bold text-white-900 mb-4 leading-tight" data-testid="post-title">
              {post.title}
            </h1>

            {/* Excerpt */}
            {post.excerpt && (
              <p className="text-xl text-gray-600 mb-6" data-testid="post-excerpt">
                {post.excerpt}
              </p>
            )}

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span data-testid="post-date">
                  {formatDate(post.published_at || post.created_at)}
                </span>
              </div>
            </div>

            {/* Tags */}
            {post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {post.tags.map((tag, i) => (
                  <Badge
                    key={i}
                    variant="secondary"
                    className="bg-blue-100 text-blue-700 hover:bg-blue-200"
                    data-testid={`tag-${i}`}
                  >
                    <Tag className="w-3 h-3 mr-1" />
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            <hr className="border-gray-200" />
          </header>

          {/* Post Content */}
          <div className="markdown-preview rounded-xl p-8 shadow-sm" data-testid="post-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkEmoji]}
              components={{
                code({node, inline, className, children, ...props}) {
                  const match = /language-(\w+)/.exec(className || '');
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={vscDarkPlus}
                      language={match[1]}
                      PreTag="div"
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                }
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* Post Footer */}
          <footer className="mt-12 pt-8 border-t border-gray-200">
            {/* Categories */}
            {post.categories.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Categories</h3>
                <div className="flex flex-wrap gap-2">
                  {post.categories.map((category, i) => (
                    <Badge key={i} variant="outline" className="border-gray-300" data-testid={`category-${i}`}>
                      {category}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Back to posts */}
            <div className="text-center">
              <Link to="/">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  View All Posts
                </Button>
              </Link>
            </div>
          </footer>
        </div>
      </article>
    </div>
  );
};

export default PostViewPage;
